/**
 * Foreground-reload effect body (issue #3280): when the app returns to the
 * foreground after being backgrounded, reload messages from the server. The
 * caller keeps the `useEffect` shell with a `[token, clearActivity]` dependency
 * array and does `return reloadMessagesOnForeground({...})`; this sets up the
 * `visibilitychange` listener and returns its cleanup.
 *
 * Web-internal module: must NOT be promoted to a shared `@xavior/*` package.
 */
import type React from 'react'
import { getConversationChatMessages } from '@/lib/api/endpoints/chat'
import { serverToClientMessage } from '../utils/message-mapping'
import type { ChatMessage } from './useChat.types'

export interface ForegroundReloadDeps {
  token: string | null
  abortControllerRef: React.MutableRefObject<AbortController | null>
  currentSessionIdRef: React.MutableRefObject<string | undefined>
  lastStreamEventAtRef: React.MutableRefObject<number>
  elapsedTimerRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  setThinking: React.Dispatch<React.SetStateAction<string | null>>
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  clearActivity: () => void
}

export function reloadMessagesOnForeground(deps: ForegroundReloadDeps): () => void {
  const {
    token,
    abortControllerRef,
    currentSessionIdRef,
    lastStreamEventAtRef,
    elapsedTimerRef,
    setMessages,
    setThinking,
    setIsStreaming,
    setIsLoading,
    setStreamingMessage,
    clearActivity,
  } = deps

  let hiddenAtMs = 0
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAtMs = Date.now()
      return
    }
    if (document.visibilityState !== 'visible') return

    // Only act if a request was in flight when we left
    if (!abortControllerRef.current) return

    const sessionIdSnapshot = currentSessionIdRef.current
    const tokenSnapshot = token
    if (!sessionIdSnapshot || !tokenSnapshot) return

    // If the stream received an event recently, it's still alive — don't abort.
    // Server heartbeats (5s cadence) now flow through the SSE parser and
    // refresh lastStreamEventAtRef (#5194), so 120s of TRUE silence is a
    // reliable dead-stream signal even during a long page/report build whose
    // content events pause for 60-90s. A dead connection stops heartbeating,
    // so it trips this window on its own — the old `msHidden >= 180s` clause
    // (abort after any 3-min background, even with fresh events) is gone: it
    // was killing healthy long builds on tab return, leaving the chat looking
    // fully stalled while the server kept building. msHidden stays logged for
    // diagnostics only.
    const msSinceLastEvent = Date.now() - lastStreamEventAtRef.current
    const msHidden = hiddenAtMs > 0 ? Date.now() - hiddenAtMs : Infinity
    if (msSinceLastEvent < 120_000) {
      console.log('[useChat] Stream still alive after tab switch, not aborting', {
        msSinceLastEvent,
        msHidden,
      })
      return
    }

    console.log('[useChat] Aborting stale stream after tab return', {
      msSinceLastEvent,
      msHidden,
    })

    abortControllerRef.current.abort()
    abortControllerRef.current = null
    setIsStreaming(false)
    setIsLoading(true)
    setStreamingMessage(null)
    setThinking(null)
    clearActivity()
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }

    // Reload messages — server may have finished processing while we were away
    getConversationChatMessages(tokenSnapshot, sessionIdSnapshot, { limit: 100 })
      .then(({ messages: serverMessages }) => {
        if (currentSessionIdRef.current !== sessionIdSnapshot) return
        const clientMessages = serverMessages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(serverToClientMessage)
        setMessages(clientMessages)
      })
      .catch((err) => {
        console.error('[useChat] Failed to reload messages after returning to foreground:', err)
      })
      .finally(() => {
        if (currentSessionIdRef.current !== sessionIdSnapshot) return
        setIsLoading(false)
      })
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
}
